// components/LectorDNIMobile.js - Versión optimizada para móvil

import React, { useState, useRef, useEffect } from 'react';
import {
  Popup,
  Tabs,
  Button,
  Toast,
  Input,
  SpinLoading,
  Space,
  ImageUploader,
  Image
} from 'antd-mobile';
import { 
  CloseOutlined,
  QrcodeOutlined,
  CameraOutlined,
  IdcardOutlined,
  BugOutlined,
  UploadOutlined,
  CheckOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import { BASE_URL } from '../utils/constants';

const LectorDNIMobile = ({ visible, onClose, onSuccess, numReserva, linea, numOcupante, nombreCliente }) => {
  const [modo, setModo] = useState('qr');
  const [loading, setLoading] = useState(false);
  const [camaraDisponible, setCamaraDisponible] = useState(true);
  const [errorCamara, setErrorCamara] = useState(null);
  const [imagenSeleccionada, setImagenSeleccionada] = useState(null);
  const [qrImageBase64, setQrImageBase64] = useState(null);
  const [paso, setPaso] = useState('captura');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const qrScannerRef = useRef(null);
  const streamRef = useRef(null);
  const containerId = `qr-reader-${Date.now()}`;

  // Verificar si la página es HTTPS (requerido para cámara en móvil)
  const esHTTPS = () => {
    return window.location.protocol === 'https:';
  };

  // Verificar permisos de cámara
  const verificarPermisosCamara = async () => {
    try {
      const result = await navigator.permissions.query({ name: 'camera' });
      return result.state === 'granted';
    } catch (error) {
      console.log('No se pudo verificar permisos:', error);
      return false;
    }
  };

  // Solicitar permisos de cámara
  const solicitarPermisosCamara = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      console.error('Error solicitando permisos:', error);
      return false;
    }
  };

  // Iniciar scanner QR con cámara (optimizado para móvil)
  const iniciarScannerQR = async () => {
    setErrorCamara(null);
    
    // Verificar HTTPS
    if (!esHTTPS()) {
      setErrorCamara('⚠️ La cámara requiere conexión HTTPS. Usa https:// para acceder a la cámara.');
      setCamaraDisponible(false);
      Toast.show({
        content: 'La cámara requiere HTTPS. Conecta mediante https://',
        icon: 'fail',
        duration: 4000
      });
      return;
    }

    // Verificar permisos
    const tienePermisos = await verificarPermisosCamara();
    if (!tienePermisos) {
      const permisosConcedidos = await solicitarPermisosCamara();
      if (!permisosConcedidos) {
        setErrorCamara('📱 Permiso de cámara denegado. Habilita el acceso en la configuración de tu navegador.');
        setCamaraDisponible(false);
        Toast.show({
          content: 'Permiso de cámara denegado. Habilítalo en la configuración.',
          icon: 'fail',
          duration: 4000
        });
        return;
      }
    }

    const element = document.getElementById(containerId);
    if (!element) {
      console.error('Elemento contenedor no encontrado');
      setErrorCamara('Error al iniciar el escáner');
      Toast.show({
        content: 'Error al iniciar el escáner',
        icon: 'fail'
      });
      return;
    }

    try {
      // Limpiar scanner anterior
      if (qrScannerRef.current) {
        try {
          await qrScannerRef.current.stop();
          await qrScannerRef.current.clear();
        } catch (e) {}
        qrScannerRef.current = null;
      }

      // Limpiar contenido del contenedor
      element.innerHTML = '';

      const html5QrCode = new Html5Qrcode(containerId);
      
      // Configuración optimizada para móvil
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        showTorchButton: true,
        rememberLastUsedCamera: true,
        supportedScanTypes: [Html5Qrcode.SCAN_TYPE_CAMERA]
      };

      // Intentar con cámara trasera primero
      let cameraId = { facingMode: "environment" };
      
      // Enumerar cámaras para seleccionar la trasera
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        
        // Buscar cámara trasera (environment)
        const backCamera = videoDevices.find(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('rear') ||
          device.label.toLowerCase().includes('environment')
        );
        
        if (backCamera) {
          cameraId = { deviceId: { exact: backCamera.deviceId } };
          console.log('📷 Cámara trasera encontrada:', backCamera.label);
        }
      } catch (e) {
        console.log('No se pudieron enumerar cámaras:', e);
      }

      await html5QrCode.start(
        cameraId,
        config,
        async (decodedText) => {
          // QR detectado - capturar frame
          try {
            const videoElement = document.querySelector(`#${containerId} video`);
            if (videoElement && videoElement.videoWidth > 0) {
              const tempCanvas = document.createElement('canvas');
              tempCanvas.width = videoElement.videoWidth;
              tempCanvas.height = videoElement.videoHeight;
              const ctx = tempCanvas.getContext('2d');
              ctx.drawImage(videoElement, 0, 0);
              
              const imageBase64 = tempCanvas.toDataURL('image/jpeg', 0.8);
              const base64Clean = imageBase64.split(',')[1];
              setQrImageBase64(base64Clean);
              
              // Guardar preview
              setImagenSeleccionada(URL.createObjectURL(
                await (await fetch(imageBase64)).blob()
              ));
              
              setPaso('confirmar');
              
              // Detener scanner
              try {
                await html5QrCode.stop();
                await html5QrCode.clear();
              } catch (e) {}
              
              Toast.show({
                content: '✅ QR capturado correctamente',
                icon: 'success'
              });
            }
          } catch (captureError) {
            console.error('Error capturando imagen:', captureError);
            Toast.show({
              content: 'Error al capturar el QR',
              icon: 'fail'
            });
          }
        },
        (errorMessage) => {
          // Ignorar errores de escaneo continuo
          if (errorMessage && !errorMessage.includes('No MultiFormat Readers') && !errorMessage.includes('decode')) {
            console.debug('Escaneando...');
          }
        }
      );
      
      qrScannerRef.current = html5QrCode;
      setCamaraDisponible(true);
      setErrorCamara(null);
      
    } catch (error) {
      console.error('Error al iniciar scanner:', error);
      
      let mensajeError = 'Error al iniciar la cámara';
      if (error.message?.includes('NotAllowedError')) {
        mensajeError = 'Permiso de cámara denegado. Habilita el acceso en la configuración.';
      } else if (error.message?.includes('NotFoundError')) {
        mensajeError = 'No se encontró ninguna cámara en este dispositivo.';
      } else if (error.message?.includes('NotReadableError')) {
        mensajeError = 'La cámara está siendo usada por otra aplicación.';
      } else if (error.message?.includes('OverconstrainedError')) {
        mensajeError = 'No se encontró una cámara compatible.';
      }
      
      setErrorCamara(mensajeError);
      setCamaraDisponible(false);
      Toast.show({
        content: mensajeError,
        icon: 'fail',
        duration: 4000
      });
    }
  };

  // Iniciar cámara para DNI físico
  const iniciarCamaraDNI = async () => {
    setErrorCamara(null);
    
    if (!esHTTPS()) {
      setErrorCamara('⚠️ La cámara requiere conexión HTTPS');
      setCamaraDisponible(false);
      Toast.show({
        content: 'La cámara requiere HTTPS',
        icon: 'fail'
      });
      return;
    }

    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCamaraDisponible(true);
      setErrorCamara(null);
      
    } catch (error) {
      console.error('Error accediendo a la cámara:', error);
      let mensajeError = 'No se pudo acceder a la cámara';
      if (error.message?.includes('NotAllowedError')) {
        mensajeError = 'Permiso de cámara denegado';
      } else if (error.message?.includes('NotFoundError')) {
        mensajeError = 'No se encontró cámara en el dispositivo';
      }
      setErrorCamara(mensajeError);
      setCamaraDisponible(false);
      Toast.show({
        content: mensajeError,
        icon: 'fail'
      });
    }
  };

  // Procesar imagen subida (para QR)
  const procesarImagenSubida = async (file) => {
    if (!file) return;
    
    setLoading(true);
    const previewUrl = URL.createObjectURL(file);
    setImagenSeleccionada(previewUrl);
    
    try {
      const base64 = await fileToBase64(file);
      const base64Clean = base64.split(',')[1];
      setQrImageBase64(base64Clean);
      setPaso('confirmar');
      
      Toast.show({
        content: '✅ Imagen cargada correctamente',
        icon: 'success'
      });
    } catch (error) {
      console.error('Error procesando imagen:', error);
      Toast.show({
        content: 'Error al procesar la imagen',
        icon: 'fail'
      });
      setImagenSeleccionada(null);
    } finally {
      setLoading(false);
    }
    
    return { url: previewUrl };
  };

  // Convertir archivo a base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Confirmar y enviar imagen QR al backend
  const confirmarQR = async () => {
    if (!qrImageBase64) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/checkin-checkout/procesar-qr`,
        { 
          qrImageBase64: qrImageBase64,
          numReserva, 
          linea, 
          numOcupante 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        Toast.show({
          content: `✅ DNI registrado: ${response.data.data.numero_documento}`,
          icon: 'success'
        });
        onSuccess(response.data.data);
        onClose();
        resetearEstado();
      } else if (response.data.needsNextPage) {
        Toast.show({
          content: '📄 Escanea el reverso del documento',
          duration: 2000
        });
        onSuccess(response.data.data);
        onClose();
      } else {
        Toast.show({
          content: response.data.error || 'Error al procesar QR',
          icon: 'fail'
        });
      }
    } catch (error) {
      console.error('Error procesando QR:', error);
      Toast.show({
        content: error.response?.data?.error || 'Error al procesar QR',
        icon: 'fail'
      });
    } finally {
      setLoading(false);
    }
  };

  // Resetear estados
  const resetearEstado = () => {
    setQrImageBase64(null);
    setImagenSeleccionada(null);
    setPaso('captura');
    setErrorCamara(null);
  };

  // Volver a capturar
  const volverACapturar = () => {
    resetearEstado();
    if (modo === 'qr' && camaraDisponible) {
      setTimeout(() => iniciarScannerQR(), 500);
    }
  };

  // Limpiar recursos al cerrar/desmontar
  useEffect(() => {
    if (!visible) {
      const limpiar = async () => {
        if (qrScannerRef.current) {
          try {
            await qrScannerRef.current.stop();
            await qrScannerRef.current.clear();
          } catch (e) {}
          qrScannerRef.current = null;
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        resetearEstado();
      };
      limpiar();
      return;
    }

    if (visible && modo === 'qr' && paso === 'captura') {
      setTimeout(() => iniciarScannerQR(), 500);
    } else if (visible && modo === 'camara' && paso === 'captura') {
      setTimeout(() => iniciarCamaraDNI(), 500);
    }

    return () => {
      const limpiar = async () => {
        if (qrScannerRef.current) {
          try {
            await qrScannerRef.current.stop();
            await qrScannerRef.current.clear();
          } catch (e) {}
          qrScannerRef.current = null;
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };
      limpiar();
    };
  }, [visible, modo, paso]);

  // Capturar foto para DNI físico
// Usar el elemento canvas en el mismo tamaño que el video y luego recortar
const capturarFotoAlternativa = async () => {
  const video = videoRef.current;
  const canvas = canvasRef.current;
  
  // Capturar frame completo
  const context = canvas.getContext('2d');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  
  // Definir el área de recorte como porcentaje del centro
  const cropPercent = 0.7; // 70% del centro
  const cropWidth = canvas.width * cropPercent+20;
  const cropHeight = canvas.height * cropPercent;
  const cropX = (canvas.width - cropWidth) / 2;
  const cropY = (canvas.height - cropHeight) / 2;
  
  // Crear nuevo canvas con el recorte
  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = cropWidth;
  croppedCanvas.height = cropHeight;
  const croppedCtx = croppedCanvas.getContext('2d');
  croppedCtx.drawImage(canvas, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
  
  // Enviar el recorte
  croppedCanvas.toBlob(async (blob) => {
    if (blob) {
      await procesarFoto(blob);
    }
  }, 'image/jpeg', 0.9);
};

const capturarFoto = async () => {
  if (!videoRef.current || !canvasRef.current) {
    Toast.show({
      content: 'Error: No se pudo acceder a la cámara',
      icon: 'fail'
    });
    return;
  }

  const video = videoRef.current;
  const canvas = canvasRef.current;
  
  // Esperar a que el video tenga dimensiones
  if (video.videoWidth === 0 || video.videoHeight === 0) {
    Toast.show({
      content: 'Esperando a que la cámara esté lista...',
      icon: 'loading'
    });
    setTimeout(() => capturarFotoAlternativa(), 500);
    return;
  }

  try {
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    
    // Buscar el marco
    const videoContainer = video.parentElement;
    const frame = videoContainer?.querySelector('.camera-frame');
    
    // Si no hay marco o no se puede calcular, capturar toda la imagen
    if (!frame) {
      console.log('⚠️ No se encontró marco, capturando imagen completa');
      const context = canvas.getContext('2d');
      canvas.width = videoWidth;
      canvas.height = videoHeight;
      context.drawImage(video, 0, 0, videoWidth, videoHeight);
      
      canvas.toBlob(async (blob) => {
        if (blob) {
          await procesarFoto(blob);
        }
      }, 'image/jpeg', 0.9);
      return;
    }
    
    // Obtener posiciones relativas
    const frameRect = frame.getBoundingClientRect();
    const videoRect = video.getBoundingClientRect();
    
    // Verificar que las posiciones son válidas
    if (frameRect.width === 0 || frameRect.height === 0) {
      console.error('Marco con dimensiones cero');
      Toast.show({
        content: 'Error: El marco no es visible',
        icon: 'fail'
      });
      return;
    }
    
    // Calcular escalas
    const scaleX = videoWidth / videoRect.width;
    const scaleY = videoHeight / videoRect.height;
    
    // Calcular coordenadas de recorte
    let cropX = (frameRect.left - videoRect.left) * scaleX;
    let cropY = (frameRect.top - videoRect.top) * scaleY;
    let cropWidth = frameRect.width * scaleX;
    let cropHeight = frameRect.height * scaleY;
    
    // Asegurar que las coordenadas están dentro de los límites
    cropX = Math.max(0, Math.min(cropX, videoWidth));
    cropY = Math.max(0, Math.min(cropY, videoHeight));
    cropWidth = Math.min(cropWidth, videoWidth - cropX);
    cropHeight = Math.min(cropHeight, videoHeight - cropY);
    
    console.log('📐 Recorte calculado:', {
      videoOriginal: `${videoWidth}x${videoHeight}`,
      videoMostrado: `${videoRect.width}x${videoRect.height}`,
      marcoMostrado: `${frameRect.width}x${frameRect.height}`,
      escala: `${scaleX.toFixed(2)}x${scaleY.toFixed(2)}`,
      recorte: `${Math.round(cropX)}, ${Math.round(cropY)}, ${Math.round(cropWidth)}x${Math.round(cropHeight)}`
    });
    
    // Crear canvas para el recorte
    const context = canvas.getContext('2d');
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    
    // Dibujar solo el área del marco
    context.drawImage(
      video,
      cropX, cropY, cropWidth, cropHeight,
      0, 0, cropWidth, cropHeight
    );
    
    // Opcional: Dibujar un rectángulo rojo en el canvas para debug
    // context.strokeStyle = 'red';
    // context.strokeRect(0, 0, cropWidth, cropHeight);
    
    // Convertir a blob
    canvas.toBlob(async (blob) => {
      if (blob) {
        console.log(`📸 Foto recortada: ${(blob.size / 1024).toFixed(2)} KB`);
        
        // Opcional: Mostrar preview del recorte
        const previewUrl = URL.createObjectURL(blob);
        setImagenSeleccionada(previewUrl);
        
        await procesarFoto(blob);
      } else {
        throw new Error('No se pudo crear la imagen');
      }
    }, 'image/jpeg', 0.9);
    
  } catch (error) {
    console.error('Error capturando foto:', error);
    Toast.show({
      content: 'Error al capturar la foto',
      icon: 'fail'
    });
  }
};

  // Procesar foto del DNI físico
  const procesarFoto = async (imageBlob) => {
    setLoading(true);
    const formData = new FormData();
    formData.append('image', imageBlob);
    formData.append('numReserva', numReserva);
    formData.append('linea', linea);
    formData.append('numOcupante', numOcupante);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/dni_delta/procesar`,
        formData,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000
        }
      );
      
      if (response.data.success) {
        Toast.show({
          content: `✅ DNI procesado: ${response.data.data.numero_documento}`,
          icon: 'success'
        });
        onSuccess(response.data.data);
        onClose();
      } else if (response.data.needsNextPage) {
        Toast.show({
          content: '📄 Escanea el reverso del DNI',
          duration: 2000
        });
        onSuccess(response.data.data);
        onClose();
      } else {
        Toast.show({
          content: response.data.error || 'Error al procesar',
          icon: 'fail'
        });
      }
    } catch (error) {
      console.error('Error procesando foto:', error);
      Toast.show({
        content: error.response?.data?.error || 'Error al procesar el DNI',
        icon: 'fail'
      });
    } finally {
      setLoading(false);
    }
  };

  // Generar datos simulados (solo para desarrollo)
  const generarDatosSimulados = () => {
    const generarLetraDNI = (numero) => {
      const letras = 'TRWAGMYFPDXBNJZSQVHLCKE';
      return letras[numero % 23];
    };
    
    const numeroDNI = Math.floor(Math.random() * 90000000) + 10000000;
    const dniCompleto = `${numeroDNI}${generarLetraDNI(numeroDNI)}`;
    
    const nombres = ['JUAN', 'MARIA', 'CARLOS', 'ANA', 'JAVIER', 'LAURA'];
    const apellidos = ['GARCIA', 'LOPEZ', 'MARTINEZ', 'SANCHEZ', 'PEREZ'];
    
    return {
      tipo_documento: 'D',
      numero_documento: dniCompleto,
      nombre: nombres[Math.floor(Math.random() * nombres.length)],
      apellido1: apellidos[Math.floor(Math.random() * apellidos.length)],
      apellido2: apellidos[Math.floor(Math.random() * apellidos.length)],
      fecha_nacimiento: '01011990',
      fecha_expedicion: '01012020',
      fecha_validez: '01012030',
      nacionalidad: 'ESP',
      sexo: Math.random() > 0.5 ? 'M' : 'F',
      domicilio: 'Calle Principal, 123',
      simulado: true
    };
  };

  const simularLectura = async () => {
    setLoading(true);
    setTimeout(() => {
      const datos = generarDatosSimulados();
      Toast.show({
        content: `🔬 SIMULACIÓN: DNI ${datos.numero_documento}`,
        icon: 'success'
      });
      onSuccess(datos);
      onClose();
      setLoading(false);
    }, 1000);
  };

  // Verificar si es usuario software
  const esUsuarioSoftware = () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.email === 'software@dapinformatica.com';
      }
    } catch (e) {}
    return false;
  };

  return (
    <Popup
      visible={visible}
      onMaskClick={onClose}
      position='bottom'
      bodyStyle={{ height: '85vh', borderRadius: '16px 16px 0 0' }}
    >
      <div style={{ padding: 16, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Registrar DNI</h3>
          <Button fill='none' onClick={onClose}>
            <CloseOutlined />
          </Button>
        </div>
        
        {/* Info del cliente */}
        <div style={{ 
          marginBottom: 16, 
          padding: 12, 
          background: '#f5f5f5', 
          borderRadius: 8,
          fontSize: 14
        }}>
          <div><strong>Reserva:</strong> #{numReserva}</div>
          <div><strong>Cliente:</strong> {nombreCliente}</div>
          {numOcupante && <div><strong>Ocupante:</strong> #{numOcupante}</div>}
        </div>
        
        {/* Aviso HTTPS si es necesario */}
        {!esHTTPS() && (
          <div style={{ 
            marginBottom: 16, 
            padding: 12, 
            background: '#fff1f0', 
            borderRadius: 8,
            fontSize: 12,
            color: '#ff4d4f',
            textAlign: 'center'
          }}>
            <span>⚠️ </span>
            La cámara requiere conexión HTTPS. Usa https:// para acceder a la cámara.
          </div>
        )}
        
        {/* Tabs */}
        {paso === 'captura' && (
          <Tabs activeKey={modo} onChange={(key) => {
            setModo(key);
            resetearEstado();
          }}>
            <Tabs.Tab title='📱 Escanear QR' key='qr'>
              <div style={{ marginTop: 16 }}>
                {errorCamara && (
                  <div style={{ 
                    padding: 12, 
                    background: '#fff1f0', 
                    borderRadius: 8,
                    marginBottom: 12,
                    fontSize: 12,
                    color: '#ff4d4f'
                  }}>
                    {errorCamara}
                  </div>
                )}
                
                {camaraDisponible && !errorCamara && (
                  <>
                    <div id={containerId} style={{ width: '100%', minHeight: '320px' }} />
                    <div style={{ 
                      marginTop: 16, 
                      padding: 12, 
                      background: '#e6f7ff', 
                      borderRadius: 8,
                      fontSize: 12,
                      color: '#666',
                      textAlign: 'center'
                    }}>
                      <CameraOutlined style={{ marginRight: 8 }} />
                      Enfoca el código QR del DNI 3.0
                    </div>
                  </>
                )}
                
                {/* Opción de subir imagen - siempre disponible */}
                <div style={{ marginTop: 16 }}>
                  <ImageUploader
                    upload={procesarImagenSubida}
                    maxCount={1}
                    accept="image/jpeg,image/png"
                  >
                    <Button block color='primary' fill='outline'>
                      <UploadOutlined /> Subir imagen con QR
                    </Button>
                  </ImageUploader>
                </div>
              </div>
            </Tabs.Tab>
            
            <Tabs.Tab title='📸 Escanear DNI' key='camara'>
              <div style={{ marginTop: 16 }}>
                {errorCamara && (
                  <div style={{ 
                    padding: 12, 
                    background: '#fff1f0', 
                    borderRadius: 8,
                    marginBottom: 12,
                    fontSize: 12,
                    color: '#ff4d4f'
                  }}>
                    {errorCamara}
                  </div>
                )}
                
                {camaraDisponible && !errorCamara && (
                  <>
                    <div style={{ 
                      position: 'relative',
                      background: '#000',
                      borderRadius: 12,
                      overflow: 'hidden',
                      aspectRatio: '4/3'
                    }}>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <canvas ref={canvasRef} style={{ display: 'none' }} />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '85%',
                        height: '70%',
                        border: '2px solid #1677ff',
                        borderRadius: 12,
                        pointerEvents: 'none'
                      }} />
                    </div>
                    
                    <Button
                      block
                      color='primary'
                      size='large'
                      onClick={capturarFotoAlternativa}
                      disabled={loading}
                      style={{ marginTop: 16 }}
                    >
                      <CameraOutlined /> Capturar documento
                    </Button>
                  </>
                )}
                
                {/* Opción de subir imagen para DNI */}
                <div style={{ marginTop: 16 }}>
                  <ImageUploader
                    upload={async (file) => {
                      await procesarFoto(file);
                      return { url: URL.createObjectURL(file) };
                    }}
                    maxCount={1}
                    accept="image/jpeg,image/png"
                  >
                    <Button block color='primary' fill='outline'>
                      <UploadOutlined /> Subir foto del DNI
                    </Button>
                  </ImageUploader>
                </div>
                
                <div style={{ 
                  marginTop: 16, 
                  padding: 12, 
                  background: '#fff7e6', 
                  borderRadius: 8,
                  fontSize: 12,
                  color: '#666'
                }}>
                  <CameraOutlined style={{ marginRight: 8 }} />
                  Coloca el DNI dentro del marco y asegura buena iluminación
                </div>
              </div>
            </Tabs.Tab>
          </Tabs>
        )}

        {/* Pantalla de confirmación */}
        {paso === 'confirmar' && (
          <div style={{ marginTop: 16, flex: 1 }}>
            <div style={{
              background: '#f6ffed',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center',
              marginBottom: 20
            }}>
              <CheckOutlined style={{ fontSize: 48, color: '#52c41a' }} />
              <h3 style={{ margin: '12px 0 8px' }}>QR Capturado</h3>
            </div>
            
            {imagenSeleccionada && (
              <div style={{ marginBottom: 20 }}>
                <Image src={imagenSeleccionada} width="100%" style={{ borderRadius: 8 }} />
              </div>
            )}
            
            <Space direction='vertical' block style={{ gap: 12 }}>
              <Button
                block
                color='primary'
                size='large'
                onClick={confirmarQR}
                loading={loading}
              >
                <CheckOutlined /> Procesar QR
              </Button>
              
              <Button
                block
                fill='outline'
                size='large'
                onClick={volverACapturar}
                disabled={loading}
              >
                <ReloadOutlined /> Volver a escanear
              </Button>
            </Space>
          </div>
        )}
        
        {/* Botón de simulación para desarrollo */}
        {esUsuarioSoftware() && paso === 'captura' && (
          <div style={{ 
            marginTop: 16, 
            paddingTop: 16, 
            borderTop: '1px solid #e5e5e5',
            textAlign: 'center'
          }}>
            <Button
              block
              color='warning'
              fill='outline'
              onClick={simularLectura}
              disabled={loading}
              style={{ borderStyle: 'dashed' }}
            >
              <BugOutlined /> 🔬 Modo Simulación
            </Button>
            <div style={{ fontSize: 10, color: '#999', marginTop: 8 }}>
              Solo visible para desarrolladores
            </div>
          </div>
        )}
      </div>
    </Popup>
  );
};

export default LectorDNIMobile;